import React from "react";
import { Button, Result } from "antd";
import { THEME_COLORS, BORDER_RADIUS } from "../utils/designSystem";

/**
 * ChunkErrorBoundary
 * Intercepts chunk loading errors (e.g., ChunkLoadError, network drops during dynamic import,
 * or post-deployment bundle hash mismatches) and prevents React Suspense from freezing
 * indefinitely on <PageLoader />.
 *
 * Behavior:
 * 1. Detects ChunkLoadError / failed module script fetch.
 * 2. Automatically reloads the page once to retrieve fresh bundles.
 * 3. If reload was already attempted within the last 15 seconds, displays a graceful
 *    recovery screen with a manual reload button instead of looping.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    const message = (error?.message || "").toLowerCase();
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      message.includes("loading chunk") ||
      message.includes("failed to fetch dynamic import") ||
      message.includes("importing a module script failed") ||
      message.includes("dynamically imported module");

    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an unhandled route error:", error, errorInfo);

    const message = (error?.message || "").toLowerCase();
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      message.includes("loading chunk") ||
      message.includes("failed to fetch dynamic import") ||
      message.includes("importing a module script failed") ||
      message.includes("dynamically imported module");

    if (isChunkError && typeof window !== "undefined") {
      const now = Date.now();
      const lastRetry = parseInt(sessionStorage.getItem("chunk_retry_timestamp") || "0", 10);

      // Auto-reload once if not attempted within last 15 seconds
      if (now - lastRetry > 15000) {
        sessionStorage.setItem("chunk_retry_timestamp", now.toString());
        console.warn("Retrying chunk load by reloading page...");
        window.location.reload();
      }
    }
  }

  handleManualReload = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("chunk_retry_timestamp");
      window.location.reload();
    }
  };

  handleGoLogin = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("chunk_retry_timestamp");
      window.location.href = "/login";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            padding: "24px",
          }}
        >
          <Result
            status="warning"
            title="Connection or Update Detected"
            subTitle={
              this.state.isChunkError
                ? "The application has been updated or your network reconnected. Please reload to load the latest version."
                : "An unexpected error occurred while loading this view. Please reload or return to login."
            }
            extra={[
              <Button
                type="primary"
                key="reload"
                size="large"
                onClick={this.handleManualReload}
                style={{
                  backgroundColor: THEME_COLORS.primary,
                  borderColor: THEME_COLORS.primary,
                  borderRadius: BORDER_RADIUS.base,
                  fontWeight: 600,
                  height: "42px",
                }}
              >
                Reload & Reconnect
              </Button>,
              <Button
                key="login"
                size="large"
                onClick={this.handleGoLogin}
                style={{
                  borderRadius: BORDER_RADIUS.base,
                  height: "42px",
                  fontWeight: 500,
                }}
              >
                Go to Login
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
